import { useState, FormEvent, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Smartphone, Mail, Key } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'https://go-auth-engine-dev-yaglve3mla-uc.a.run.app';
// Spring is proxied at the same host (relative URL). Override via VITE_SPRING_API_URL for standalone dev.
const SPRING_API_URL = import.meta.env.VITE_SPRING_API_URL || '';

// ── WebAuthn base64url helpers ───────────────────────────────────────────────
function base64urlToUint8Array(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
  const binary = atob(padded);
  const buf = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return view;
}

function uint8ArrayToBase64url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

interface MFAMethod {
  type: 'totp' | 'sms' | 'email' | 'webauthn';
  label: string;
  icon: typeof Shield;
  primary?: boolean;
}

const MFA_METHODS: Record<string, MFAMethod> = {
  totp: { type: 'totp', label: 'Authenticator App', icon: Smartphone, primary: true },
  sms: { type: 'sms', label: 'SMS Code', icon: Mail },
  email: { type: 'email', label: 'Email Code', icon: Mail },
  webauthn: { type: 'webauthn', label: 'Security Key', icon: Key },
};

export default function MFAChallengePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedMethod, setSelectedMethod] = useState<'totp' | 'sms' | 'email' | 'webauthn'>('totp');
  const [codeSent, setCodeSent] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [webAuthnLoading, setWebAuthnLoading] = useState(false);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get state from navigation or query params
  const state = location.state as { 
    sessionId?: string; 
    methods?: string[];
    userId?: string;
    arToken?: string;
    tenantId?: string;
  } | null;
  
  const sessionId = state?.sessionId || searchParams.get('session_id');
  const availableMethods = state?.methods || ['totp', 'sms', 'email'];
  const userId = state?.userId || searchParams.get('user_id');
  // OAuth2 flow params passed through from LoginPage
  const arToken = state?.arToken || searchParams.get('ar');
  const tenantId = state?.tenantId || searchParams.get('tenant_id') || 'demo-salon';

  useEffect(() => {
    if (!sessionId) {
      navigate('/login', { replace: true });
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    // Auto-focus first input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleSendCode = async (method: 'sms' | 'email') => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/mfa/send-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          method,
          user_id: userId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send code');
      }

      setCodeSent(true);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMFA = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/mfa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantId,
        },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          method: selectedMethod,
          code: code,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setAttemptsRemaining(errorData.attempts_remaining);
        throw new Error(errorData.error_description || errorData.message || 'Invalid verification code');
      }

      const verifyData = await response.json();
      const { access_token } = verifyData as { access_token: string };

      if (!access_token) {
        throw new Error('MFA verification succeeded but no token was returned.');
      }

      // Complete the OAuth2 authorization code flow — same as LoginPage step 2.
      if (arToken) {
        const completeResp = await fetch(`${API_BASE_URL}/oauth2/authorize/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Tenant-ID': tenantId,
          },
          body: JSON.stringify({ ar: arToken, access_token }),
        });

        const completeData = await completeResp.json();

        if (!completeResp.ok) {
          const msg = completeData?.error_description || completeData?.message || 'Authorization code exchange failed.';
          throw new Error(msg);
        }

        const { redirect_to, response_mode, form_params } = completeData as {
          redirect_to: string;
          response_mode?: string;
          form_params?: Record<string, string>;
        };
        if (response_mode === 'form_post' && form_params) {
          const { action, ...fields } = form_params;
          const fm = document.createElement('form');
          fm.method = 'POST';
          fm.action = action;
          Object.entries(fields).forEach(([k, v]) => {
            if (v !== undefined && v !== null) {
              const input = document.createElement('input');
              input.type = 'hidden'; input.name = k; input.value = String(v);
              fm.appendChild(input);
            }
          });
          document.body.appendChild(fm);
          fm.submit();
          return;
        }
        if (!redirect_to) throw new Error('Server did not return a redirect URL.');
        window.location.href = redirect_to;
      } else {
        // Direct login (no OIDC flow) — redirect to home
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Paste handling - distribute digits across inputs
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newCode = digits.split('');
      
      newCode.forEach((digit, i) => {
        if (inputRefs.current[i]) {
          inputRefs.current[i]!.value = digit;
        }
      });
      
      setCode(newCode.join(''));
      
      // Focus last filled input
      const lastIndex = Math.min(digits.length - 1, 5);
      inputRefs.current[lastIndex]?.focus();
      return;
    }

    // Single digit input
    const newCodeArray = code.split('');
    newCodeArray[index] = value;
    const newCode = newCodeArray.join('');
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleMethodChange = (method: 'totp' | 'sms' | 'email' | 'webauthn') => {
    setSelectedMethod(method);
    setCode('');
    setError('');
    setCodeSent(false);
    
    // Auto-send code for SMS/email
    if (method === 'sms' || method === 'email') {
      handleSendCode(method);
    }
  };

  // ── WebAuthn ceremony ──────────────────────────────────────────────────────
  const handleWebAuthn = async () => {
    // Guard: WebAuthn requires a secure context (HTTPS) and browser support.
    if (!window.PublicKeyCredential) {
      setError('Your browser does not support hardware security keys. Use a different MFA method or a modern browser over HTTPS.');
      return;
    }

    setWebAuthnLoading(true);
    setError('');
    try {
      // 1. Fetch assertion options from Spring
      const optionsResp = await fetch(`${SPRING_API_URL}/api/v1/webauthn/authenticate/options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
        body: JSON.stringify({ username: userId }),
      });
      if (!optionsResp.ok) {
        throw new Error(`Failed to get WebAuthn options: ${optionsResp.status}`);
      }
      const options = await optionsResp.json();

      // 2. Invoke the browser WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: base64urlToUint8Array(options.challenge) as unknown as ArrayBuffer,
          rpId: options.rpId,
          allowCredentials: (options.allowCredentials ?? []).map((c: { id: string; type: string }) => ({
            id: base64urlToUint8Array(c.id) as unknown as ArrayBuffer,
            type: c.type,
          })),
          userVerification: (options.userVerification as UserVerificationRequirement) ?? 'preferred',
          timeout: options.timeout ?? 60000,
        },
      }) as PublicKeyCredential;

      if (!credential) throw new Error('No credential returned by browser');

      const assertionResponse = credential.response as AuthenticatorAssertionResponse;

      // 3. Post assertion to Go engine → Spring
      const verifyResp = await fetch(`${API_BASE_URL}/api/v1/auth/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          method: 'webauthn',
          webauthn_response: {
            challengeId: options.challenge,
            credentialId: uint8ArrayToBase64url(credential.rawId),
            clientDataJSON: uint8ArrayToBase64url(assertionResponse.clientDataJSON),
            authenticatorData: uint8ArrayToBase64url(assertionResponse.authenticatorData),
            signature: uint8ArrayToBase64url(assertionResponse.signature),
          },
        }),
      });

      if (!verifyResp.ok) {
        const errBody = await verifyResp.json().catch(() => ({}));
        throw new Error((errBody as { error_description?: string }).error_description ?? 'WebAuthn verification failed');
      }

      const result = await verifyResp.json() as { access_token?: string };
      if (!result.access_token) throw new Error('No access token returned');

      if (arToken) {
        const completeResp = await fetch(`${API_BASE_URL}/oauth2/authorize/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-ID': tenantId },
          body: JSON.stringify({ ar: arToken, access_token: result.access_token }),
        });
        const completeBody = await completeResp.json() as { redirect_to?: string; response_mode?: string; form_params?: Record<string, string> };
        if (completeBody.response_mode === 'form_post' && completeBody.form_params) {
          const { action, ...fields } = completeBody.form_params;
          const fm = document.createElement('form');
          fm.method = 'POST'; fm.action = action;
          Object.entries(fields).forEach(([k, v]) => {
            if (v !== undefined && v !== null) {
              const input = document.createElement('input');
              input.type = 'hidden'; input.name = k; input.value = String(v);
              fm.appendChild(input);
            }
          });
          document.body.appendChild(fm); fm.submit(); return;
        }
        if (!completeBody.redirect_to) throw new Error('No redirect URL');
        window.location.href = completeBody.redirect_to;
      } else {
        navigate('/');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'WebAuthn failed';
      // "NotAllowedError" means user cancelled — give a friendlier message
      setError(msg.includes('NotAllowedError') ? 'Verification cancelled. Try again.' : msg);
    } finally {
      setWebAuthnLoading(false);
    }
  };
  // ── end WebAuthn ───────────────────────────────────────────────────────────

  const getMethodIcon = (method: string) => {
    const Icon = MFA_METHODS[method]?.icon || Shield;
    return <Icon className="oauth-icon" size={20} />;
  };

  return (
    <div className="auth-container">
      <div className="auth-header">
        <Shield size={48} color="#667eea" style={{ margin: '0 auto 16px' }} />
        <div className="auth-logo">Two-Factor Authentication</div>
        <div className="auth-subtitle">Verify your identity to continue</div>
      </div>

      {availableMethods.length > 1 && (
        <div className="mfa-method-selector">
          {availableMethods.map((method) => (
            <button
              key={method}
              type="button"
              className={`mfa-method-btn ${selectedMethod === method ? 'active' : ''}`}
              onClick={() => handleMethodChange(method as 'totp' | 'sms' | 'email' | 'webauthn')}
            >
              {getMethodIcon(method)}
              <span>{MFA_METHODS[method]?.label || method}</span>
            </button>
          ))}
        </div>
      )}

      <form onSubmit={handleVerifyMFA}>
        {selectedMethod === 'totp' && (
          <>
            <div className="mfa-instructions">
              <p>Enter the 6-digit code from your authenticator app</p>
            </div>
            
            <div className="code-input-group">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  className="code-input"
                  value={code[index] || ''}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  required
                />
              ))}
            </div>
          </>
        )}

        {(selectedMethod === 'sms' || selectedMethod === 'email') && (
          <>
            <div className="mfa-instructions">
              {codeSent ? (
                <p>✓ Code sent to your {selectedMethod === 'sms' ? 'phone' : 'email'}</p>
              ) : (
                <p>We'll send a verification code to your {selectedMethod === 'sms' ? 'phone' : 'email'}</p>
              )}
            </div>

            {!codeSent ? (
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => handleSendCode(selectedMethod)}
                disabled={loading}
              >
                {loading ? 'Sending...' : `Send Code via ${selectedMethod.toUpperCase()}`}
              </button>
            ) : (
              <>
                <div className="code-input-group">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]"
                      maxLength={1}
                      className="code-input"
                      value={code[index] || ''}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      required
                    />
                  ))}
                </div>

                <button
                  type="button"
                  className="resend-link"
                  onClick={() => handleSendCode(selectedMethod)}
                  disabled={loading}
                >
                  Resend code
                </button>
              </>
            )}
          </>
        )}

        {selectedMethod === 'webauthn' && (
          <div className="mfa-instructions">
            <p>Use your security key or fingerprint sensor to verify your identity.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleWebAuthn}
              disabled={webAuthnLoading}
              style={{ marginTop: '12px', width: '100%' }}
            >
              {webAuthnLoading ? 'Waiting for security key…' : 'Verify with Security Key'}
            </button>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
            {attemptsRemaining !== null && (
              <div className="attempts-warning">
                {attemptsRemaining > 0 
                  ? `${attemptsRemaining} attempts remaining`
                  : 'Account temporarily locked. Try again in 15 minutes.'}
              </div>
            )}
          </div>
        )}

        {(selectedMethod === 'totp' || codeSent) && (
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={loading || code.length !== 6}
          >
            {loading ? (
              <>
                <div className="loading-spinner" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </button>
        )}
      </form>

      <div className="footer-links">
        <a href="/login" className="footer-link">
          ← Back to login
        </a>
        <span style={{ color: 'var(--text-secondary)' }}>·</span>
        <a href="/help" className="footer-link">
          Need help?
        </a>
      </div>
    </div>
  );
}
